import React, { useMemo } from 'react'
import ExtraTextEditor from '../../ExtraTextEditor'
import { STEP_TYPE_ENUM, TASK_TYPE_ENUM } from 'services/api/constants'
import Field from 'components/Shared/Fields'
import { BaseEditorContainer, EditorContentCardContainer } from '../../styles'
import BaseDivider from '../../BaseDivider'
import { Section, Title } from '../../../Shared'
import { IgFbPost, IgStory, TikTokPostAndInstagramReel } from './RequirementBlock'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import Header from '../../Header'
import { useNewTranslation } from 'hooks'
import { fieldIdentifiers } from 'components/Shared/Comment'

interface SubmitContentEditorProps {
    type: TASK_TYPE_ENUM
    stepType: STEP_TYPE_ENUM
    stepNumber: number
    requiredFieldNames: string[]
    uuid?: string
    showBrief?: boolean
}

const SubmitContentEditor: React.FC<SubmitContentEditorProps> = ({
    type,
    stepType,
    stepNumber,
    requiredFieldNames,
    uuid,
    showBrief,
}) => {
    const [t] = useNewTranslation()

    const { stepTitlePlaceholderText } = useMemo(() => {
        let stepTitlePlaceholderKey = ''
        switch (type) {
            case TASK_TYPE_ENUM.IG_POST:
            case TASK_TYPE_ENUM.IG_POST_V02:
                stepTitlePlaceholderKey = 'campaign.step.submitInstagramPostContent'
                break
            case TASK_TYPE_ENUM.IG_STORY:
            case TASK_TYPE_ENUM.IG_STORY_V02:
                stepTitlePlaceholderKey = 'campaign.step.submitInstagramStoryContent'
                break
            case TASK_TYPE_ENUM.IG_REEL:
                stepTitlePlaceholderKey = 'campaign.step.submitInstagramReelContent'
                break
            case TASK_TYPE_ENUM.FB_POST:
            case TASK_TYPE_ENUM.FB_POST_V02:
                stepTitlePlaceholderKey = 'campaign.step.submitFacebookPostContent'
                break
            case TASK_TYPE_ENUM.TIKTOK_POST:
                stepTitlePlaceholderKey = 'campaign.step.submitTikTokContent'
                break
        }

        return {
            stepTitlePlaceholderText: t(stepTitlePlaceholderKey),
        }
    }, [t, type])

    return (
        <BaseEditorContainer id={uuid && uuid}>
            <EditorContentCardContainer>
                {!showBrief && <Header taskType={type} />}
                <Title taskType={type} stepType={stepType} stepNumber={stepNumber} />
                <CampaignEditorInputFieldContainer>
                    <Field.TextInputField
                        className={'input-area'}
                        label={t('campaignEditor.createTask.enterStepTitleHeader')}
                        placeholder={stepTitlePlaceholderText}
                        name={`stepData.${stepType}.data.title`}
                        isRequired={requiredFieldNames.includes('title')}
                        width={'100%'}
                        identifier={`${uuid}_${fieldIdentifiers.stepTitle}`}
                        isReadOnly={showBrief}
                    />
                </CampaignEditorInputFieldContainer>
                <BaseDivider />
                <Section
                    title={t('campaignEditor.createTask.enterSubmissionRequirementHeader')}
                    subtitle={t('campaignEditor.createTask.enterSubmissionRequirementSubtext')}
                >
                    <CampaignEditorInputFieldContainer>
                        <Field.TextAreaField
                            label={t('campaignEditor.createTask.enterDescriptionHeader')}
                            name={`stepData.${stepType}.data.submission_requirement_description`}
                            isRequired={requiredFieldNames.includes('submission_requirement_description')}
                            width={'100%'}
                            minTextAreaHeight={76}
                            identifier={`${uuid}_${fieldIdentifiers.stepDescription}`}
                            isReadOnly={showBrief}
                        />
                    </CampaignEditorInputFieldContainer>
                </Section>
                {[
                    TASK_TYPE_ENUM.IG_POST,
                    TASK_TYPE_ENUM.IG_POST_V02,
                    TASK_TYPE_ENUM.FB_POST,
                    TASK_TYPE_ENUM.FB_POST_V02,
                ].includes(type) ? (
                    <IgFbPost
                        type={type}
                        stepType={stepType}
                        requiredFieldNames={requiredFieldNames}
                        stepUuid={uuid}
                        showBrief={showBrief}
                    />
                ) : [TASK_TYPE_ENUM.IG_STORY, TASK_TYPE_ENUM.IG_STORY_V02].includes(type) ? (
                    <IgStory
                        type={type}
                        stepType={stepType}
                        requiredFieldNames={requiredFieldNames}
                        stepUuid={uuid}
                        showBrief={showBrief}
                    />
                ) : [TASK_TYPE_ENUM.TIKTOK_POST, TASK_TYPE_ENUM.IG_REEL].includes(type) ? (
                    <TikTokPostAndInstagramReel
                        type={type}
                        stepType={stepType}
                        requiredFieldNames={requiredFieldNames}
                        stepUuid={uuid}
                        showBrief={showBrief}
                    />
                ) : null}
                {!showBrief && (
                    <CampaignEditorInputFieldContainer>
                        <ExtraTextEditor name={`stepData.${stepType}.data.extra_data`} isRequired />
                    </CampaignEditorInputFieldContainer>
                )}
            </EditorContentCardContainer>
        </BaseEditorContainer>
    )
}
export default SubmitContentEditor
